import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPaperclip,
  FiDownload,
  FiPlus,
  FiFile,
  FiImage,
  FiVideo,
  FiMusic,
  FiCode,
  FiArchive,
  FiX,
} from "react-icons/fi";
import { roomFileService } from "../../../../services/rooms";
import "./TaskAttachments.css";

const TaskAttachments = ({ task, roomId, onAttachFile, canAttach }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [roomFiles, setRoomFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    if (task.attachments && task.attachments.length > 0) {
      loadAttachmentDetails();
    }
  }, [task.attachments]);

  const loadAttachmentDetails = async () => {
    try {
      setLoading(true);
      const promises = task.attachments.map((attId) =>
        roomFileService.getAttachmentDetails(roomId, attId)
      );
      const results = await Promise.all(promises);
      const validAttachments = results
        .filter((r) => r.success)
        .map((r) => r.data);
      setAttachments(validAttachments);
    } catch (error) { /* ignored */ } finally {
      setLoading(false);
    }
  };

  const loadRoomFiles = async () => {
    try {
      setLoadingFiles(true);
      const response = await roomFileService.getAttachments(roomId, { limit: 50 });
      if (response.success) {
        setRoomFiles(response.data);
      }
    } catch (error) { /* ignored */ } finally {
      setLoadingFiles(false);
    }
  };

  const handleShowFilePicker = () => {
    loadRoomFiles();
    setShowFilePicker(true);
  };

  const handleAttachFile = async (fileId) => {
    try {
      await onAttachFile(fileId);
      setShowFilePicker(false);
    } catch (error) { /* ignored */ }
  };

  const handleDownload = async (attachmentId) => {
    try {
      await roomFileService.downloadFile(roomId, attachmentId);
    } catch (error) { /* ignored */ }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      document: FiFile,
      image: FiImage,
      video: FiVideo,
      audio: FiMusic,
      code: FiCode,
      archive: FiArchive,
    };
    return icons[fileType] || FiFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className="rta-container">
      {/* Attachments List */}
      {loading ? (
        <div className="rta-loading">
          <div className="rta-spinner"></div>
          <span>Loading files...</span>
        </div>
      ) : attachments.length === 0 ? (
        <div className="rta-empty">
          <div className="rta-empty-icon">
            <FiPaperclip size={32} />
          </div>
          <p>No files attached</p>
          <span>Attach files from your lab</span>
        </div>
      ) : (
        <div className="rta-list">
          <AnimatePresence>
            {attachments.map((file, index) => {
              const IconComponent = getFileIcon(file.fileType);
              return (
                <motion.div
                  key={file._id}
                  className="rta-item"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="rta-icon">
                    <IconComponent size={20} />
                  </div>
                  <div className="rta-info">
                    <span className="rta-name">{file.name}</span>
                    <span className="rta-meta">
                      {formatFileSize(file.size)} • {file.uploadedBy?.name || "Unknown"}
                    </span>
                  </div>
                  <button
                    className="rta-download"
                    onClick={() => handleDownload(file._id)}
                    title="Download"
                  >
                    <FiDownload size={16} />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Attach Button */}
      {canAttach && (
        <button className="rta-add-btn" onClick={handleShowFilePicker}>
          <FiPlus size={18} />
          <span>Attach File</span>
        </button>
      )}

      {/* File Picker Modal */}
      <AnimatePresence>
        {showFilePicker && (
          <div className="rta-picker-overlay" onClick={() => setShowFilePicker(false)}>
            <motion.div
              className="rta-picker"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rta-picker-header">
                <h3>Select File</h3>
                <button 
                  className="rta-picker-close"
                  onClick={() => setShowFilePicker(false)}
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="rta-picker-content">
                {loadingFiles ? (
                  <div className="rta-loading">
                    <div className="rta-spinner"></div>
                    <span>Loading lab files...</span>
                  </div>
                ) : roomFiles.length === 0 ? (
                  <div className="rta-picker-empty">
                    <p>No files in this lab</p>
                  </div>
                ) : (
                  <div className="rta-picker-list">
                    {roomFiles.map((file) => {
                      const IconComponent = getFileIcon(file.fileType);
                      const isAttached = task.attachments?.includes(file._id);
                      return (
                        <button
                          key={file._id}
                          className={`rta-picker-item ${isAttached ? "rta-picker-item--attached" : ""}`}
                          onClick={() => !isAttached && handleAttachFile(file._id)}
                          disabled={isAttached}
                        >
                          <div className="rta-picker-icon">
                            <IconComponent size={18} />
                          </div>
                          <div className="rta-picker-info">
                            <span className="rta-picker-name">{file.name}</span>
                            <span className="rta-picker-meta">{formatFileSize(file.size)}</span>
                          </div>
                          {isAttached && <span className="rta-picker-badge">Attached</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskAttachments;
